import React from 'react';
import { PositionRank, positionHoverLabel } from '../GovernanceAnalyticsAiVisibility.utils';

/**
 * A subject's position rank, as a badge.
 *
 * The ordinal is the headline because that is the question a reader asks ("where do we come?"),
 * and the mean it was derived from sits on the hover rather than in the badge: two decimals of a
 * mean is the detail you go looking for, not the thing you scan a column for.
 *
 * NEVER `#0` AND NEVER A DEFAULT. A subject no answer positioned has no rank, and the badge says
 * "not positioned" rather than inventing one. A zero would read as a real placing, and a fallback
 * ordinal would put an unmeasured subject in a league table it never entered.
 *
 * The hover uses the native `title` attribute deliberately, matching `GapConfidenceBadge` — the
 * react-bootstrap overlay mispositions inside the resized iframe.
 *
 * HEIGHT IS FIXED BY THE TEXT, not by the badge. Both states are one `inline-flex` line box at
 * `text-xs` with `py-0.5`, which is shorter than the `text-sm` cells beside it, so adding this
 * column cannot change a row's height. No `min-h`, no `h-full`, no flex growth.
 */
export function PositionRankBadge({ rank, untracked }: { rank?: PositionRank; untracked?: boolean }) {
  if (untracked) {
    // Unranked BY RULE, not for want of a measurement: the "no term assigned" row aggregates every
    // untracked subject, so it has no single placing to report. Saying "not positioned" here would
    // claim something about the data that was never measured.
    return (
      <span className="inline-flex items-center text-xs text-slate-400" title="Untracked subjects are not ranked">
        —
      </span>
    );
  }

  if (!rank || rank.rank === undefined) {
    return (
      <span className="inline-flex items-center text-xs font-semibold text-slate-400" title="No answer positioned this subject">
        Not positioned
      </span>
    );
  }

  const hover = positionHoverLabel(rank);

  // Settled ranks read as a placing; provisional ones stay grey and carry their n in the badge, so
  // the reader sees the rank AND that it rests on very few answers instead of one or the other.
  return rank.settled ? (
    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-700" title={hover}>
      #{rank.rank}
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold bg-slate-50 text-slate-400 border border-slate-200"
      title={hover}
    >
      #{rank.rank}
      <span className="font-normal">n={rank.positionRunCount ?? 0}</span>
    </span>
  );
}
